import React, { useState, useCallback, useEffect } from 'react';
import { evaluate } from './calcLogic';
import './Calculator.css';

const KEYS = [
  ['C', '⌫', '%', '÷'],
  ['7', '8', '9', '×'],
  ['4', '5', '6', '−'],
  ['1', '2', '3', '+'],
  ['0', '.', '=', '^'],
];

const SCIENTIFIC = [
  ['π', 'e', '(', ')'],
  ['sin', 'cos', 'tan', '√'],
  ['log', 'ln', '(', ')'],
];

function mapDisplayOp(s) {
  const map = { '÷': '/', '×': '*', '−': '-', '√': 'sqrt(' };
  return map[s] ?? s;
}

const KEY_MAP = {
  Escape: 'C',
  Backspace: '⌫',
  Enter: '=',
  '=': '=',
  '%': '%',
  '/': '÷',
  '*': '×',
  '-': '−',
  '+': '+',
  '.': '.',
  '^': '^',
  '(': '(',
  ')': ')',
  '0': '0', '1': '1', '2': '2', '3': '3', '4': '4',
  '5': '5', '6': '6', '7': '7', '8': '8', '9': '9',
};

export default function CalculatorPanel({ isOpen }) {
  const [expr, setExpr] = useState('');
  const [result, setResult] = useState('');

  const handleKey = useCallback(
    (key) => {
      if (key === 'C') {
        setExpr('');
        setResult('');
        return;
      }
      if (key === '⌫') {
        setExpr((e) => e.slice(0, -1));
        setResult('');
        return;
      }
      if (key === '=') {
        if (!expr) return;
        let s = expr.replace(/[÷×−]/g, (m) => mapDisplayOp(m));
        s = s.replace(/(\d)%/g, '$1/100');
        const res = evaluate(s);
        setResult(res);
        return;
      }
      const append = mapDisplayOp(key);
      setExpr((e) => e + append);
      setResult('');
    },
    [expr]
  );

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => {
      const key = KEY_MAP[e.key];
      if (key) {
        e.preventDefault();
        handleKey(key);
        return;
      }
      if (e.key.length === 1 && /[0-9]/.test(e.key)) {
        e.preventDefault();
        handleKey(e.key);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleKey]);

  if (!isOpen) return null;

  return (
    <div className="calc-panel">
      <div className="calc-display">
        <div className="calc-expression">{(expr || '0').replace(/sqrt\(/g, '√(')}</div>
        <div className="calc-result">{result || ''}</div>
      </div>
      <div className="calc-keys">
        <div className="calc-row calc-scientific">
          {SCIENTIFIC.flat().map((k, idx) => (
            <button
              key={`sci-${idx}-${k}`}
              type="button"
              className="calc-key calc-key--sci"
              onClick={() => handleKey(k)}
            >
              {k}
            </button>
          ))}
        </div>
        {KEYS.map((row, i) => (
          <div key={i} className="calc-row">
            {row.map((k) => (
              <button
                key={k}
                type="button"
                className={`calc-key ${k === '=' ? 'calc-key--equals' : ''} ${k === 'C' ? 'calc-key--clear' : ''}`}
                onClick={() => handleKey(k)}
              >
                {k}
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
